import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button, Form, Result, Skeleton, Space } from 'antd'
import B2BLayout from '../../layouts/b2b'
import fetchAPI, { IHttpError } from '../../../lib/utils/fetch-api'
import { PlusOutlined, FilterOutlined } from '@ant-design/icons'
import { objectToUrl } from '../../pages_v2/utils'
import FiltersList from '../../components/v2/filters'
import { createSelector } from 'reselect'
import { useSelector } from 'react-redux'
import {
  getJsonSettingsByKey,
  getColumnOrder,
  TableSettingsButton,
  TransferItem,
} from '../../components/v2/table-settings-button'
import { getPrintPath } from '../../components/v2/export-excel-button'
import { HrPrintButton } from './hr-print-button'
import FiltersDrawer, { FiltersDrawerRef } from '../../components/v2/filters-drawer'
import { FiltersFastButtons } from '../../components/v2/filters-fast-buttons'
import DataTable from '../../components/data-table'
import StickyToolbar from '../../layouts/b2b/sticky-toolbar'
import { useMediaQuery } from 'react-responsive'
import { useHeaderActions } from '../../contexts/header-actions-context'
import { HeaderAction } from '../../contexts/header-actions-context/interface'
import { getFilters } from './filters'
import { getColumns } from './columns'
import { getMobileFields } from './mobile-fields'
import './index.css'
import { ResultStatusType } from 'antd/lib/result'
import { HStack } from '../../ui/Stack'
import { hasPermission } from 'src/react-app/lib/permissions'
import { Permissions } from 'src/react-app/constants/permissions'

interface EnumItem {
  label: string
  value: number | string
}

interface Enums {
  [x: string]: EnumItem[]
}

const createSession = createSelector(
  (state: any) => state.session,
  sessionData => sessionData,
)

interface IError {
  code?: ResultStatusType
  message?: string
}

export default function AdminObjectsListPage() {
  const { sessionData } = useSelector(createSession)
  const navigate = useNavigate()
  const location = useLocation()
  const { pathname, search } = location
  const [filtersForm] = Form.useForm()

  const headerRef = useRef<HTMLDivElement | null>(null)
  const filtersDrawerRef = useRef<FiltersDrawerRef | null>(null)
  const tableSettingsRef = useRef<{ open: () => void } | null>(null)

  const [stages, setStages] = useState<any[]>([])
  const [enums, setEnums] = useState<Enums>({})
  const [data, setData] = useState<any[]>([])
  const [pageError, setPageError] = useState<null | IError>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [urlParams] = useState(Object.fromEntries(new URLSearchParams(search)))
  const jsonSettings = useSelector(state => getJsonSettingsByKey(state, pathname))

  const initialPageSize = (() => {
    const savedPageSize = localStorage.getItem('hrPageSize')
    const parsedPageSize = Number(savedPageSize)
    return parsedPageSize && !isNaN(parsedPageSize) && parsedPageSize <= 150 ? parsedPageSize : 50
  })()

  const [pagination, setPagination] = useState({
    current: Number(urlParams?.page) || 1,
    pageSize: initialPageSize,
    position: ['bottomCenter'],
    showSizeChanger: true,
    total: 0,
  })

  const [sorter, setSorter] = useState<any>({
    sort_field: urlParams?.field,
    sort_order: urlParams?.order,
  })

  const [currentFilters, setCurrentFilters] = useState<any>({
    id: urlParams?.id,
    fio: urlParams?.fio,
    phone: urlParams?.phone,
    created_by: urlParams?.created_by && Number(urlParams?.created_by),
    manager_id: urlParams?.manager_id && Number(urlParams?.manager_id),
    department: urlParams?.department && Number(urlParams?.department),
    vacancy: urlParams?.vacancy && Number(urlParams?.vacancy),
    stage: urlParams?.stage && urlParams?.stage,
    city_id: urlParams?.city_id && Number(urlParams?.city_id),
    region_id: urlParams?.region_id && Number(urlParams?.region_id),
    cancel_reason: urlParams?.cancel_reason && Number(urlParams?.cancel_reason),
    datetime_interview: urlParams?.datetime_interview,
    interview_type: urlParams?.interview_type,
    created_at: urlParams?.created_at,
    start_probation_period: urlParams?.start_probation_period,
    end_probation_period: urlParams?.end_probation_period,
  })

  const isMobile = useMediaQuery({ maxWidth: 768 })
  const { setHeaderActions } = useHeaderActions()

  const breadcrumbs = useMemo(
    () => [
      {
        href: pathname,
        title: 'Учет кандидатов',
      },
    ],
    [pathname],
  )

  const convertEnumObject = (data: Record<string, any>): Record<string, EnumItem[]> => {
    const convertedData: Record<string, EnumItem[]> = {}

    Object.keys(data).forEach(key => {
      const value = data[key]
      if (Array.isArray(value)) {
        convertedData[key] = value.map((item: { value: string; id: string }) => ({
          label: item.value,
          value: parseInt(item.id) || item.id,
        }))
      } else if (typeof value === 'object') {
        convertedData[key] = Object.keys(value).map(id => ({
          label: value[id],
          value: parseInt(id) || id,
        }))
      }
    })
    return convertedData
  }

  useEffect(() => {
    async function fetchData() {
      setLoading(true)
      const [stagesList, enumsData] = await Promise.all([
        fetchAPI(`/api/stages-list?name=hr`, { method: 'GET' }),
        fetchAPI('/api/hr/request/enum'),
      ])
      const e = convertEnumObject(enumsData.enum)
      setEnums(e)
      if (stagesList) {
        setStages(stagesList as any[])
      }
      setLoading(false)
    }
    fetchData()
  }, [])

  const getData = useCallback(async () => {
    if (!stages || stages.length === 0) {
      return
    }
    setLoading(true)
    setPageError(null)
    const currentUrlParams = Object.fromEntries(new URLSearchParams(search))
    fetchAPI(`/api/hr/request${objectToUrl(currentUrlParams)}`)
      .then(({ data, pagination, sorter }) => {
        if (data) {
          setData(
            data.map(item => ({
              ...item,
              stage: stages.find(stage => stage.code === item.stage) || item.stage,
            })),
          )
        }
        setPagination(prevState => ({
          ...prevState,
          ...pagination,
        }))
        setSorter(sorter)
      })

      .then(() => {
        setLoading(false)
      })
      .catch(e => {
        console.error(e)
        setPageError({
          message: (e as IHttpError)?.message || 'Неизвестная ошибка',
          code: ((e as IHttpError)?.statusCode || 'error') as ResultStatusType,
        })
        setLoading(false)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [search, stages])

  useEffect(() => {
    if (stages.length > 0) {
      getData()
    }
  }, [stages, getData])

  useEffect(() => {
    const params = new URLSearchParams(search)
    const nextFilters: any = {
      id: params.get('id') || undefined,
      fio: params.get('fio') || undefined,
      phone: params.get('phone') || undefined,
      created_by: params.get('created_by') ? Number(params.get('created_by')) : undefined,
      manager_id: params.get('manager_id') ? Number(params.get('manager_id')) : undefined,
      department: params.get('department') ? Number(params.get('department')) : undefined,
      vacancy: params.get('vacancy') ? Number(params.get('vacancy')) : undefined,
      stage: params.get('stage') || undefined,
      city_id: params.get('city_id') ? Number(params.get('city_id')) : undefined,
      region_id: params.get('region_id') ? Number(params.get('region_id')) : undefined,
      cancel_reason: params.get('cancel_reason') ? Number(params.get('cancel_reason')) : undefined,
      datetime_interview: params.get('datetime_interview') || undefined,
      interview_type: params.get('interview_type') || undefined,
      created_at: params.get('created_at') || undefined,
      start_probation_period: params.get('start_probation_period') || undefined,
      end_probation_period: params.get('end_probation_period') || undefined,
    }
    setCurrentFilters(nextFilters)
  }, [search])

  const canDelete = hasPermission(sessionData?.permissionsWithScope, Permissions.ACCESS_HR)

  /**
   * Удаляет запись
   * @param id
   */
  const remove = useCallback(
    (id: number) => {
      fetchAPI(`/api/hr/request/${id}`, { method: 'DELETE' })
        .then(() => {
          getData()
        })
        .catch(e => {
          console.error({ e })
        })
    },
    [getData],
  )

  const columns = useMemo(
    () => getColumns(sorter, enums, stages, canDelete, remove),
    [sorter, enums, stages, canDelete, remove],
  )

  const viewColumns = useMemo(() => {
    const columnOrder = getColumnOrder(jsonSettings)

    if (columnOrder.length > 0) {
      const result: any[] = []
      for (let i = 0; i < columnOrder.length; i++) {
        const item = columns.find((column: any) => columnOrder[i] === column.dataIndex)
        if (item) {
          result.push(item)
        }
      }

      const actionColumn = columns.find((column: any) => column.key === 'operation')
      if (actionColumn && !result.find((col: any) => col?.key === 'operation')) {
        result.push(actionColumn)
      }

      return result
    }
    return columns
  }, [columns, jsonSettings])

  const settingsColumns = useMemo(() => {
    return columns
      .filter((column: any) => column?.key !== 'operation')
      .map(column => ({
        key: (column as any)?.dataIndex,
        title: column?.title,
      }))
  }, [columns]) as TransferItem[]

  const mobileFields = useMemo(() => getMobileFields(enums), [enums])

  const rowClassName = useCallback(record => {
    return record?.['is_read'] ? 'table-row-dark' : 'table-row-light'
  }, [])

  const handleTableChange = useCallback(
    (_pagination, _filters, sorter) => {
      const currentUrlParams = Object.fromEntries(new URLSearchParams(search))
      setPagination(prevState => ({ ...prevState, ...pagination }))
      const current: Record<string, any> = {
        ...currentUrlParams,
        sort_order: sorter?.order,
        sort_field: sorter?.field,
        page: pagination.current,
        pageSize: pagination.pageSize,
      }
      if (current?.page === 1) {
        delete current.page
      }
      if (current?.pageSize === 10) {
        delete current.pageSize
      }
      if (current?.sort_order === undefined) {
        delete current.sort_order
        delete current.sort_field
      }
      const urlParams = new URLSearchParams(current).toString()
      navigate({ search: urlParams })
    },
    [search, navigate, pagination],
  )

  const handlePageChange = (page: number, pageSize: number) => {
    setPagination(prevState => ({
      ...prevState,
      current: Number(page) || 1,
      pageSize: pageSize > 150 ? 150 : pageSize,
    }))

    if (!isNaN(pageSize)) {
      localStorage.setItem('measurementsPageSize', String(pageSize))
    }

    const currentUrlParams = new URLSearchParams(search)
    if (page && page !== 1) {
      currentUrlParams.set('page', page.toString())
    } else {
      currentUrlParams.delete('page')
    }
    if (!isNaN(pageSize)) {
      if (pageSize && pageSize !== 50) {
        currentUrlParams.set('pageSize', pageSize.toString())
      } else {
        currentUrlParams.delete('pageSize')
      }
    }
    navigate({ search: currentUrlParams.toString() })
  }

  /**
   * Отслеживает состояние фильтров
   * @param filter
   */
  const changeFiltersHandler = useCallback(
    filter => {
      const tmpFilter = { ...(filter || {}), pageSize: pagination.pageSize }
      const newUrlParams = new URLSearchParams(tmpFilter).toString()
      navigate({ search: newUrlParams })
    },
    [pagination, navigate],
  )

  const excelPath = useMemo(() => {
    return getPrintPath('/api/hr/request', new URLSearchParams(search))
  }, [search])

  const handleRowClick = useCallback(
    item => {
      if (item['id']) {
        navigate(`/hr/${item['id']}`)
      }
    },
    [navigate],
  )

  const onButtonCreate = useCallback(() => navigate(`/hr/new`), [navigate])

  const filters = useMemo(() => {
    return getFilters({ currentFilters, availableFilters: enums, stages })
  }, [currentFilters, enums, stages])

  // Быстрые фильтры по стадиям
  const fastButtons = useMemo(() => {
    const stageButtons = (stages || []).map((s: any) => ({
      name: s.name,
      code: 'stage',
      value: s.code,
      style: s.style,
    }))

    const myResponsesButton = {
      name: 'Мои отклики',
      code: 'manager_id',
      value: String(sessionData?.user?.id || ''),
      style: { background: '#1890ff', color: '#fff' },
    }

    return sessionData?.user?.id ? [myResponsesButton, ...stageButtons] : stageButtons
  }, [stages, sessionData?.user?.id])

  useEffect(() => {
    const actions: HeaderAction[] = []

    if (filtersDrawerRef) {
      actions.push({
        key: 'filters',
        icon: <FilterOutlined />,
        onClick: () => {
          filtersDrawerRef.current?.toggle()
        },
        tooltip: 'Фильтры',
      })
    }

    setHeaderActions(actions)

    return () => {
      setHeaderActions([])
    }
  }, [setHeaderActions, isMobile])

  return (
    <B2BLayout breadcrumbs={breadcrumbs} isViewScrollButton pageTitle={'Учет кандидатов'}>
      {loading && <Skeleton active />}
      {!loading && pageError && (
        <Result status={pageError.code} title="Ошибка" subTitle={pageError.message} />
      )}
      {!loading && !pageError && (
        <>
          <StickyToolbar
            ref={headerRef}
            collapsible={
              <Space
                style={{ width: '100%', justifyContent: 'space-between' }}
                align={'start'}
                wrap
              >
                <Space align={'start'} wrap>
                  <Button onClick={onButtonCreate}>
                    <PlusOutlined /> Создать
                  </Button>
                  <FiltersList
                    filters={filters.filter(filter =>
                      ['id', 'stage', 'phone'].includes(filter.name),
                    )}
                    onChange={changeFiltersHandler}
                    form={filtersForm}
                  />
                </Space>
                <HStack gap={'8'}>
                  <Button
                    type={'primary'}
                    onClick={() => {
                      navigate('/hr/plans')
                    }}
                  >
                    Планы
                  </Button>
                  <FiltersDrawer
                    filters={filters}
                    onChange={changeFiltersHandler}
                    currentFilters={currentFilters}
                    ref={filtersDrawerRef}
                  />
                </HStack>
              </Space>
            }
            persistent={
              isMobile ? (
                <FiltersFastButtons filters={fastButtons} onChange={changeFiltersHandler} />
              ) : (
                <Space
                  style={{ width: '100%', justifyContent: 'space-between' }}
                  align={'center'}
                  wrap
                >
                  <FiltersFastButtons filters={fastButtons} onChange={changeFiltersHandler} />
                  <Space align={'center'} wrap>
                    <HrPrintButton
                      path={excelPath}
                      columns={viewColumns}
                      enums={enums}
                      stages={stages}
                    />
                    <TableSettingsButton items={settingsColumns} ref={tableSettingsRef} />
                  </Space>
                </Space>
              )
            }
          />
          <DataTable
            columns={viewColumns}
            mobileFields={mobileFields}
            data={data}
            loading={loading}
            rowKey={r => String(r.id)}
            highlightLastRowPrefix={'hr'}
            pagination={pagination}
            onRowClick={handleRowClick}
            onPageChange={handlePageChange}
            onTableChange={handleTableChange}
            className={'hr-table'}
            headerRef={headerRef}
            maxLines={1}
            bordered
            scroll={{ x: 'max-content' }}
            rowClassName={rowClassName}
          />
        </>
      )}
    </B2BLayout>
  )
}
